import axios from 'axios';
import {
  aggregateResultStatusByTest,
  aggregateDecisionDistribution,
  countSignificantTests,
  summarizeRecentReports,
  aggregateTaxonomyWinRates,
  aggregateMonthlyOutcomes,
  aggregateWinRateTrend,
  aggregateAnalystLeaderboard,
  aggregateMetricHeatmap,
  computeLifecycleStats,
} from '../utils/reportStatsUtils';
import { PAGE_TYPES, CHANGE_TYPES } from '../utils/reportTaxonomy';

let API_URL;
if (process.env.REACT_APP_DEV==='dev') {
  API_URL = process.env.REACT_APP_API_URL;
} else {
  API_URL = process.env.REACT_APP_API_BACKEND_URL;
}


/**
 * Tüm raporları getirir
 * @param {Array} brandIds - Kullanıcının erişebileceği marka ID'leri
 * @returns {Promise<Array>} Raporlar dizisi
 */
export const getReports = async (brandIds = [], brandId = null) => {
  try {
    let query = '';
    if (brandId) {
      query = `?brand=${brandId}`;
    } else if (brandIds.length > 0) {
      query = `?brands=${brandIds.join(',')}`;
    }
    const response = await axios.get(`${API_URL}/reports${query}`);
    return response.data.data;
  } catch (error) {
    console.error('Error fetching reports:', error);
    throw error;
  }
};

/**
 * Markalara göre gruplandırılmış raporları getirir
 * @param {Array} brandIds - Kullanıcının erişebileceği marka ID'leri
 * @returns {Promise<Array>} Gruplandırılmış raporlar
 */
export const getGroupedReports = async (brandIds = []) => {
  try {
    // Marka ID'leri varsa, query parametresi olarak ekle
    const query = brandIds.length > 0 ? `?brands=${brandIds.join(',')}` : '';
    const response = await axios.get(`${API_URL}/reports${query}`);
    return response.data.data;
  } catch (error) {
    console.error('Error fetching grouped reports:', error);
    throw error;
  }
};

/**
 * Belirli bir markaya ait raporları getirir
 * @param {string} brand - Marka adı
 * @returns {Promise<Array>} Raporlar dizisi
 */
export const getReportsByBrand = async (brand) => {
  try {
    const response = await axios.get(`${API_URL}/brand/${brand}`);
    return response.data.data;
  } catch (error) {
    console.error(`Error fetching reports for brand ${brand}:`, error);
    throw error;
  }
};

/**
 * Belirli bir raporu getirir
 * @param {string} id - Rapor ID
 * @param {Array} brandIds - Kullanıcının erişebileceği marka ID'leri
 * @returns {Promise<Object>} Rapor detayları
 */
export const getReportById = async (id, brandIds = []) => {
  try {
    // Marka ID'leri varsa, query parametresi olarak ekle
    const query = brandIds.length > 0 ? `?brands=${brandIds.join(',')}` : '';
    const response = await axios.get(`${API_URL}/reports/${id}${query}`);
    return response.data.data;
  } catch (error) {
    console.error(`Error fetching report ${id}:`, error);
    throw error;
  }
};

/**
 * Rapor metriklerini getirir
 * @param {string} id - Rapor ID
 * @returns {Promise<Object>} Rapor metrikleri
 */
export const getReportMetrics = async (id, metric) => {
  try {
    const query = metric ? `?metric=${encodeURIComponent(metric)}` : '';
    const response = await axios.get(`${API_URL}/reports/${id}/metrics${query}`);
    return response.data.data;
  } catch (error) {
    console.error(`Error fetching metrics for report ${id}:`, error);
    throw error;
  }
};

/**
 * Yeni rapor oluşturur veya mevcut raporu günceller
 * @param {Object} reportData - Rapor verileri
 * @returns {Promise<Object>} Oluşturulan/güncellenen rapor
 */
export const createReport = async (reportData) => {
  try {
    const response = await axios.post(API_URL, reportData);
    return response.data;
  } catch (error) {
    console.error('Error creating/updating report:', error);
    throw error;
  }
};

/**
 * Rapor bilgilerini günceller
 * @param {string} id - Rapor ID
 * @param {Object} reportData - Güncellenecek rapor verileri
 * @returns {Promise<Object>} Güncellenmiş rapor
 */
export const updateReport = async (id, reportData) => {
  try {
    const response = await axios.put(`${API_URL}/${id}`, reportData);
    return response.data.data;
  } catch (error) {
    console.error(`Error updating report ${id}:`, error);
    throw error;
  }
};

/**
 * Rapor durumunu günceller
 * @param {string} id - Rapor ID
 * @param {string} status - Yeni durum ('Canlı', 'Durduruldu', 'Taslak')
 * @returns {Promise<Object>} Güncellenmiş rapor
 */
export const updateReportStatus = async (id, status) => {
  try {
    const response = await axios.put(`${API_URL}/reports/${id}/status`, { status });
    return response.data;
  } catch (error) {
    console.error(`Error updating status for report ${id}:`, error);
    throw error;
  }
};

/**
 * Bulk update report statuses (max 100 ids)
 * @param {string[]} reportIds
 * @param {string} status - 'Canlı' | 'Durduruldu' | 'Taslak'
 */
export const bulkUpdateReportStatus = async (reportIds, status) => {
  try {
    const response = await axios.patch(`${API_URL}/reports/bulk-status`, { reportIds, status });
    return response.data;
  } catch (error) {
    console.error('Error bulk updating report statuses:', error);
    throw error;
  }
};

/**
 * Update CRO story metadata fields on a report
 * @param {string} id - Report ID
 * @param {Object} metadata - hypothesis, decision, tags, pageType, changeType, etc.
 */
export const updateReportMetadata = async (id, metadata) => {
  try {
    const response = await axios.patch(`${API_URL}/reports/${id}/metadata`, metadata);
    return response.data.data;
  } catch (error) {
    console.error(`Error updating metadata for report ${id}:`, error);
    throw error;
  }
};

/**
 * Regenerate structured AI summary for a report
 * @param {string} id - Report ID
 * @param {number} metricIndex - Analysis index to summarize
 * @param {Object} options - promptTemplate, customInstructions
 */
export const regenerateAISummary = async (id, metricIndex = 0, options = {}) => {
  try {
    const response = await axios.post(`${API_URL}/reports/${id}/ai-summary`, {
      metricIndex,
      promptTemplate: options.promptTemplate,
      customInstructions: options.customInstructions,
    });
    return response.data;
  } catch (error) {
    console.error(`Error regenerating AI summary for report ${id}:`, error);
    throw error;
  }
};

/** Fetch AI prompt template list */
export const fetchAIPromptTemplates = async () => {
  const response = await axios.get(`${API_URL}/reports/ai-prompt-templates`);
  return response.data?.data || [];
};

/**
 * Pattern library — tag/taxonomy aggregates + filtered report list
 * @param {Object} filters - brand, tag, pageType, changeType, result, excludeId, limit
 */
export const getReportPatterns = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.set(key, value);
      }
    });
    const query = params.toString() ? `?${params.toString()}` : '';
    const response = await axios.get(`${API_URL}/reports/patterns${query}`);
    return response.data.data;
  } catch (error) {
    console.error('Error fetching report patterns:', error);
    throw error;
  }
};

/** Create draft report from calculator pre-test plan */
export const createPlannedReport = async (payload) => {
  try {
    const response = await axios.post(`${API_URL}/reports/plan`, payload);
    return response.data.data;
  } catch (error) {
    console.error('Error creating planned report:', error);
    throw error;
  }
};

/** Snapshot + decision lifecycle reminders */
export const getLifecycleReminders = async () => {
  try {
    const response = await axios.get(`${API_URL}/reports/lifecycle/reminders`);
    return response.data.data;
  } catch (error) {
    console.error('Error fetching lifecycle reminders:', error);
    throw error;
  }
};

/** Preview assembled AI prompt without calling OpenAI */
export const previewAIPrompt = async (payload) => {
  const response = await axios.post(`${API_URL}/reports/ai-comment`, {
    ...payload,
    previewOnly: true,
  });
  return response.data?.prompt || '';
};

/**
 * Create a read-only share link for a report
 */
export const createShareLink = async (id, expiryDays = 7) => {
  try {
    const response = await axios.post(`${API_URL}/reports/${id}/share`, { expiryDays });
    return response.data;
  } catch (error) {
    console.error(`Error creating share link for report ${id}:`, error);
    throw error;
  }
};

/**
 * Fetch a report via public share token (no auth required)
 */
export const getSharedReport = async (token) => {
  try {
    const response = await axios.get(`${API_URL}/reports/shared/${token}`);
    return response.data.data;
  } catch (error) {
    console.error(`Error fetching shared report ${token}:`, error);
    throw error;
  }
};

/**
 * Raporu siler
 * @param {string} id - Rapor ID
 * @returns {Promise<boolean>} İşlem başarılı mı?
 */
export const deleteReport = async (id) => {
  try {
    await axios.delete(`${API_URL}/reports/${id}`);
    return true;
  } catch (error) {
    console.error(`Error deleting report ${id}:`, error);
    throw error;
  }
};

/**
 * Dashboard istatistiklerini getirir
 * @param {Array} brandIds - Kullanıcının erişebileceği marka ID'leri
 * @returns {Promise<Object>} Dashboard istatistikleri
 */
export const getDashboardStats = async (brandIds = [], brandId = null) => {
  try {
    try {
      let query = '';
      if (brandId) {
        query = `?brand=${brandId}`;
      } else if (brandIds.length > 0) {
        query = `?brands=${brandIds.join(',')}`;
      }
      const response = await axios.get(`${API_URL}/reports/stats/dashboard${query}`);
      return response.data.data;
    } catch (apiError) {
      console.warn('Dashboard stats API endpoint not available, generating stats from reports');
      
      const allReports = await getReports(brandIds, brandId);
      
      if (!Array.isArray(allReports)) {
        throw new Error('Reports data is not an array');
      }
      
      // İstatistikleri hesapla
      const totalReports = allReports.length;
      
      // Anlamlı sonuçları bul
      const significantResults = countSignificantTests(allReports);

      const activeTests = allReports.filter(report =>
        report.status === 'Canlı'
      ).length;
      
      // Nivo Charts için ek grafikler
      // 1. Rapor durumlarına göre dağılım (pie chart için)
      const statusDistribution = [
        { id: 'Canlı', label: 'Canlı', value: allReports.filter(report => report.status === 'Canlı').length },
        { id: 'Taslak', label: 'Taslak', value: allReports.filter(report => report.status === 'Taslak').length },
        { id: 'Durduruldu', label: 'Durduruldu', value: allReports.filter(report => report.status === 'Durduruldu').length }
      ];
      
      // 2. Performans sonuçlarına göre dağılım 
      const performanceResults = {
        positive: 0,
        negative: 0,
        neutral: 0
      };
      
      allReports.forEach(report => {
        if (report.analysis && report.analysis.length > 0) {
          // En son analizi al
          const latestAnalysis = report.analysis.sort((a, b) => 
            new Date(b.date || 0) - new Date(a.date || 0)
          )[0];
          
          if (latestAnalysis && latestAnalysis.improvement !== undefined) {
            if (latestAnalysis.improvement > 0) {
              performanceResults.positive++;
            } else if (latestAnalysis.improvement < 0) {
              performanceResults.negative++;
            } else {
              performanceResults.neutral++;
            }
          }
        }
      });
      
      const performanceDistribution = [
        { id: 'Pozitif', label: 'Pozitif', value: performanceResults.positive },
        { id: 'Negatif', label: 'Negatif', value: performanceResults.negative },
        { id: 'Nötr', label: 'Nötr', value: performanceResults.neutral }
      ];
      
      // 2.1. ResultStatus — test bazlı (her rapor tek bucket)
      const { counts: resultStatusResults, testsWithResult } =
        aggregateResultStatusByTest(allReports);

      let totalMetrics = 0;
      allReports.forEach((report) => {
        if (report.analysis?.length) {
          totalMetrics += report.analysis.filter((a) => a.resultStatus).length;
        }
      });

      const resultStatusDistribution = [
        { id: 'Kazandı', label: 'Kazandı', value: resultStatusResults.kazandi },
        { id: 'Kaybetti', label: 'Kaybetti', value: resultStatusResults.kaybetti },
        { id: 'Etkisiz', label: 'Etkisiz', value: resultStatusResults.etkisiz },
      ];

      const metricsStats = {
        totalReportCount: allReports.length,
        totalMetricCount: totalMetrics,
        testsWithResultCount: testsWithResult,
        winningTestCount: resultStatusResults.kazandi,
        losingTestCount: resultStatusResults.kaybetti,
        inconclusiveTestCount: resultStatusResults.etkisiz,
        significantTestCount: significantResults,
        reportsWithResultCount: testsWithResult,
      };

      const decisionDistribution = aggregateDecisionDistribution(allReports);
      const recentReports = summarizeRecentReports(allReports, 5);
      const taxonomyPageType = aggregateTaxonomyWinRates(allReports, 'pageType', PAGE_TYPES);
      const taxonomyChangeType = aggregateTaxonomyWinRates(allReports, 'changeType', CHANGE_TYPES);
      const monthlyOutcomes = aggregateMonthlyOutcomes(allReports);
      const winRateTrend = aggregateWinRateTrend(allReports);
      const analystLeaderboard = aggregateAnalystLeaderboard(allReports);
      const metricHeatmap = aggregateMetricHeatmap(allReports);
      
      // 3. Son 6 ayda oluşturulan rapor sayısı (bar chart için)
      const now = new Date();
      
      // Son 6 ay için ay isimleri
      const months = [];
      for (let i = 0; i < 6; i++) {
        const month = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const monthName = month.toLocaleString('tr-TR', { month: 'long' });
        months.unshift({ 
          month: monthName, 
          year: month.getFullYear(),
          index: month.getMonth()
        });
      }
      
      // Aylık rapor sayısını hesapla
      const monthlyReports = months.map(monthObj => {
        const count = allReports.filter(report => {
          const reportDate = new Date(report.createdAt);
          return reportDate.getMonth() === monthObj.index && 
                 reportDate.getFullYear() === monthObj.year;
        }).length;
        
        return {
          month: monthObj.month,
          count: count
        };
      });

      // 4. Markaya göre rapor dağılımı
      const brandCounts = {};
      
      // Raporları markalarına göre grupla
      allReports.forEach(report => {
        let brandName = 'Diğer';
        if (report.brand) {
          brandName = typeof report.brand === 'object' ? (report.brand.name || 'Diğer') : 'Diğer';
        }
        
        if (!brandCounts[brandName]) {
          brandCounts[brandName] = {
            id: brandName,
            label: brandName,
            value: 0
          };
        }
        
        brandCounts[brandName].value += 1;
      });
      
      const brandDistribution = Object.values(brandCounts)
        .sort((a, b) => b.value - a.value)
        .slice(0, 5); // İlk 5 marka
      
      // Manuel oluşturulmuş dashboard istatistikleri
      const winRate =
        testsWithResult > 0
          ? Math.round((resultStatusResults.kazandi / testsWithResult) * 1000) / 10
          : 0;

      const testVelocity = { created: 0, completed: 0, periodDays: 30 };
      const lifecycle = computeLifecycleStats(allReports, testVelocity);

      return {
        totalReports,
        significantResults,
        activeTests,
        recentReports,
        metricsStats,
        croKpis: {
          winRate,
          winningTestCount: resultStatusResults.kazandi,
          testsWithResult,
          avgUplift: 0,
          testVelocity,
          brandLeaderboard: [],
          portfolioYearlyRevenue: 0,
        },
        lifecycle,
        charts: {
          statusDistribution,
          performanceDistribution,
          resultStatusDistribution,
          decisionDistribution,
          monthlyReports,
          monthlyOutcomes,
          taxonomyPageType,
          taxonomyChangeType,
          winRateTrend,
          analystLeaderboard,
          metricHeatmap,
          brandDistribution
        }
      };
    }
  } catch (error) {
    console.error('Error fetching dashboard stats:', error);
    // API hatası durumunda kullanıcı deneyimini bozmamak için hata fırlatmak yerine
    // varsayılan/fallback veri döndürebiliriz
    console.warn('Using fallback data for dashboard stats');
    return {
      totalReports: 0,
      significantResults: 0,
      activeTests: 0,
      recentReports: [],
      metricsStats: {
        totalReportCount: 0,
        totalMetricCount: 0,
        testsWithResultCount: 0,
        winningTestCount: 0,
        losingTestCount: 0,
        inconclusiveTestCount: 0,
        significantTestCount: 0,
      },
      croKpis: {
        winRate: 0,
        avgUplift: 0,
        testVelocity: { created: 0, completed: 0, periodDays: 30 },
        brandLeaderboard: [],
        portfolioYearlyRevenue: 0,
      },
      lifecycle: {
        avgTestDurationDays: 0,
        testsWithDuration: 0,
        draftTests: 0,
        activeTests: 0,
        stoppedTests: 0,
        testVelocity: { created: 0, completed: 0, periodDays: 30 },
      },
      charts: {
        statusDistribution: [
          { id: 'Canlı', label: 'Canlı', value: 0 },
          { id: 'Taslak', label: 'Taslak', value: 0 },
          { id: 'Durduruldu', label: 'Durduruldu', value: 0 }
        ],
        performanceDistribution: [
          { id: 'Pozitif', label: 'Pozitif', value: 0 },
          { id: 'Negatif', label: 'Negatif', value: 0 },
          { id: 'Nötr', label: 'Nötr', value: 0 }
        ],
        resultStatusDistribution: [
          { id: 'Kazandı', label: 'Kazandı', value: 0 },
          { id: 'Kaybetti', label: 'Kaybetti', value: 0 },
          { id: 'Etkisiz', label: 'Etkisiz', value: 0 }
        ],
        decisionDistribution: [
          { id: 'Uygula', label: 'Uygula', value: 0 },
          { id: 'Reddet', label: 'Reddet', value: 0 },
          { id: 'Tekrar Test', label: 'Tekrar Test', value: 0 },
          { id: 'Beklemede', label: 'Beklemede', value: 0 },
        ],
        monthlyReports: Array(6).fill().map((_, idx) => {
          const month = new Date();
          month.setMonth(month.getMonth() - idx);
          return {
            month: month.toLocaleString('tr-TR', { month: 'long' }),
            count: 0
          };
        }).reverse(),
        monthlyOutcomes: Array(6).fill().map((_, idx) => {
          const month = new Date();
          month.setMonth(month.getMonth() - idx);
          return {
            month: month.toLocaleString('tr-TR', { month: 'long' }),
            Kazandı: 0,
            Kaybetti: 0,
            Etkisiz: 0,
          };
        }).reverse(),
        taxonomyPageType: [],
        taxonomyChangeType: [],
        winRateTrend: Array(6)
          .fill()
          .map((_, idx) => {
            const month = new Date();
            month.setMonth(month.getMonth() - (5 - idx));
            return {
              month: month.toLocaleString('tr-TR', { month: 'long' }),
              winRate: 0,
              testsWithResult: 0,
              won: 0,
            };
          }),
        analystLeaderboard: [],
        metricHeatmap: [],
        brandDistribution: [{ id: 'Diğer', label: 'Diğer', value: 0 }]
      }
    };
  }
}; 